import React from 'react';
import { DcaGroupCard } from '../../../types/monitor.types';
import RProgressBar from './RProgressBar';
import DcaProgressBar from './DcaProgressBar';

interface Props {
  group: DcaGroupCard;
}

const DcaGroupCardComponent: React.FC<Props> = ({ group }) => {
  const isBuy = group.direction === 'BUY';
  const isProfit = group.groupPL >= 0;
  const isBuilding = group.dcaStatus === 'BUILDING';

  const tierLabel = group.tier === 0 ? 'No Tier' : group.tier === 1 ? 'Tier 1' : `Tier ${group.tier}`;
  const tierClass = group.tier === 0 ? 'tier-none' : group.tier === 1 ? 'tier-1' : 'tier-2';

  const decimals = group.avgEntryPrice < 10 ? 5 : 2;

  return (
    <div className={`position-card ${isProfit ? 'card-profit' : 'card-loss'}`}>
      <div className="card-header">
        <div className="card-model">
          {group.model}
          <span className="dca-entries-badge">
            {group.dcaEntriesFilled}/{group.dcaMaxEntries}
          </span>
        </div>
        <div className="card-header-right">
          <span className={`dca-status-badge ${isBuilding ? 'dca-building' : 'dca-active'}`}>
            {group.dcaStatus}
          </span>
          <span className={`direction-badge ${isBuy ? 'dir-buy' : 'dir-sell'}`}>
            {group.direction}
          </span>
        </div>
      </div>

      <div className="card-instrument">{group.instrument}</div>

      <div className="card-prices">
        <div className="price-row">
          <span className="price-label">Avg Entry</span>
          <span className="price-value">{group.avgEntryPrice.toFixed(decimals)}</span>
        </div>
        <div className="price-row">
          <span className="price-label">Current</span>
          <span className="price-value">{group.currentPrice.toFixed(decimals)}</span>
        </div>
      </div>

      <div className="card-pl-row">
        <span className={`card-pl ${isProfit ? 'pl-positive' : 'pl-negative'}`}>
          ${group.groupPL.toFixed(2)}
        </span>
        <span className="card-r-value">({group.groupCurrentR.toFixed(2)}R)</span>
      </div>

      <RProgressBar currentR={group.groupCurrentR} tier={group.tier} tier1ThresholdR={group.tier1ThresholdR} exitMode={group.exitMode} takeProfitR={group.takeProfitR} />
      {isBuilding && (
        <DcaProgressBar
          dcaDropR={group.dcaDropR ?? 0}
          dcaNextTriggerR={group.dcaNextTriggerR ?? null}
          dcaHardStopR={group.dcaHardStopR ?? 1.5}
          entriesFilled={group.dcaEntriesFilled}
        />
      )}

      <div className="card-details">
        <div className="detail-row">
          <span className={`tier-badge ${tierClass}`}>{tierLabel}</span>
          <span className="detail-value">R = ${group.groupRValue.toFixed(0)}</span>
        </div>
        {isBuilding && group.dcaStopProximityR != null && (
          <div className="detail-row">
            <span className="detail-label">To Stop</span>
            <span className="detail-value dca-stop-proximity">{group.dcaStopProximityR.toFixed(2)}R</span>
          </div>
        )}
        {group.protectedProfit > 0 && (
          <div className="detail-row">
            <span className="detail-label">Protected</span>
            <span className="detail-value protected-value">${group.protectedProfit.toFixed(2)}</span>
          </div>
        )}
        {group.stopLevel > 0 && (
          <div className="detail-row">
            <span className="detail-label">Stop</span>
            <span className="detail-value">{group.stopLevel.toFixed(decimals)}</span>
          </div>
        )}
        <div className="detail-row">
          <span className="detail-label">Total Size</span>
          <span className="detail-value">{group.totalSize}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Duration</span>
          <span className="detail-value">{group.duration}</span>
        </div>
      </div>

      {group.entries.length > 1 && (
        <div className="dca-entries-detail">
          {group.entries.map((e, i) => (
            <div key={e.tradeId} className="dca-entry-row">
              <span className="dca-entry-label">E{i + 1}</span>
              <span className="dca-entry-price">{e.entryPrice.toFixed(decimals)}</span>
              <span className="dca-entry-size">{e.size}</span>
              <span className={`dca-entry-pl ${e.currentPL >= 0 ? 'pl-positive' : 'pl-negative'}`}>
                ${e.currentPL.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DcaGroupCardComponent;
